import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    Grid,
    InputLabel,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IPagination, usePagination } from '../../../../hook/usePagination';
import { ColumnsData, Table } from '../../../Tables/Table';
import { formatDate, showToastMessage, useDispatchPromise } from '../../../../utils/AppUtils';
import { CustomPagination } from '../../../Tables/Pagination';
import {
    Search as SearchIcon,
    Sort as SortIcon,
    Close as CloseIcon,
    FileDownload as ExportIcon,
    EmojiEvents as AwardIcon,
    VideoLibrary as SelectionIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import _ from 'lodash';
import {
    IManageContestSubmissions,
    TCampSortOrder,
    TCategoriesSummary,
    TSortBy,
} from '../../../../redux/contest/types';
import { useDispatch, useSelector } from 'react-redux';
import { setmedia, downloadProject, getProjectDetails, setAutoPlay } from '../../../../redux/project/actions';
import { setMute } from '../../../../redux/player/action';
import { navigate } from '../../../../redux/navigator/action';
import { replaceRouteParam } from '../../../../utils/routesProcessor';
import { ROUTES } from '../../../../types/global/routes.types';
import {
    awardPrize,
    getContestManage,
    requestSubmissionPayment,
    sendWinnersEmail,
} from '../../../../redux/contest/actions';
import { IContest } from '../../../../types/global/helper';
import { PrizeAwardModal } from './PrizeAwardModal';
import { OfficialSelectionMenu } from './OfficialSelectionMenu';
import { managedCampaignSelector } from '../../../../pages/Contests/selector';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import { getIdForIbjects } from '../../../../utils/AppUtils';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';
import {
    brandContestFilterControlSx,
    brandContestFilterFieldsRowSx,
    brandContestFilterSearchSx,
    brandContestFilterSelectSx,
    brandContestTableCellTextSx,
    brandContestTableShellSx,
    editorialSelectMenuProps,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { BrandContestSubmissionsList } from './BrandContestSubmissionsList';
import { formatJuryScore } from './submissionObsidianShared';

interface IContestSubmissionsTabProps {
    submissions?: IManageContestSubmissions[];
    contestId?: string;
    contest?: IContest;
    awardCategories?: TCategoriesSummary;
    pagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearchChange: (searchText: string) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onSortByChange: (sortBy: TSortBy) => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onClearSearch: () => void;
    onClearStartDate: () => void;
    onClearEndDate: () => void;
    onExportCSV: (submissions: any) => void;
    /** Brand contest manage drawer — pill filters + luxe table chrome. */
    visualVariant?: 'default' | 'brand';
    /** When true, host can open Official Selection to pre-judge entries before jury assignment. */
    preselectionEnabled?: boolean;
    /**
     * Parent page already loaded manage data (e.g. brand hub). Skips mount refetch and
     * routes search/sort/pagination through parent handlers to avoid duplicate getContestManage calls.
     */
    useParentManageData?: boolean;
}

// Sort options
const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: 'Oldest First' },
    { value: 'createdAt_asc', label: 'Newest First' },
    { value: 'votes_desc', label: 'Most Votes' },
    { value: 'votes_asc', label: 'Least Votes' },
    { value: 'jury_score_desc', label: 'Highest Jury Score' },
    { value: 'jury_score_asc', label: 'Lowest Jury Score' },
];

export const ContestSubmissionsTab: React.FC<IContestSubmissionsTabProps> = ({
    submissions = [],
    contestId,
    contest,
    awardCategories,
    onExportCSV,
    pagination,
    visualVariant = 'default',
    useParentManageData = false,
    onSearchChange,
    onSortOrderChange,
    onSortByChange,
    onRowsChange,
    onPageChange,
    preselectionEnabled: preselectionEnabledProp,
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const preselectionEnabled = preselectionEnabledProp ?? Boolean(contest?.preselection_enabled);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [sortOrder, setSortOrder] = useState<TCampSortOrder | undefined>();
    const [sortBy, setSortBy] = useState<TSortBy | undefined>();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [prizeAwardModalOpen, setPrizeAwardModalOpen] = useState(false);
    const [officialSelectionOpen, setOfficialSelectionOpen] = useState(false);
    const [winnersEmailDialogOpen, setWinnersEmailDialogOpen] = useState(false);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const { managedContest } = useSelector(managedCampaignSelector);

    // Check if all awards have been assigned (same logic as EsContestSubmission)
    const hasActiveSubmissionFilters = useMemo(
        () => Boolean(debouncedSearchValue.trim()) || selectedCategory !== 'all',
        [debouncedSearchValue, selectedCategory],
    );

    const submissionsEmptyMessage = useMemo(
        () => (hasActiveSubmissionFilters ? 'No submissions match your filters.' : 'No submissions yet'),
        [hasActiveSubmissionFilters],
    );

    const canSendWinnerEmail = useMemo(() => {
        if (!contest || !managedContest?.awards) {
            return false;
        }

        const emailNotSent = !contest.winner_email_sent || contest.winner_email_sent === undefined;
        const hasAssignedAwards = (managedContest.awards.awards || []).some(Boolean);

        // Allow once at least one prize is assigned — do not require every category.
        return emailNotSent && hasAssignedAwards;
    }, [contest, managedContest?.awards]);

    const unassignedAwardCategories = useMemo(() => {
        const categories = awardCategories?.categories || [];
        const awards = managedContest?.awards?.awards || [];
        if (!categories.length) return [];
        return categories.filter(category => {
            const categoryId = String(category._id);
            return !awards.some(award => {
                if (!award) return false;
                return String(getIdForIbjects(award.award_category) || '') === categoryId;
            });
        });
    }, [awardCategories?.categories, managedContest?.awards?.awards]);

    const winnersEmailDialogBody = useMemo(() => {
        const base = 'You can test this email by first sending it to yourself, or send it directly to the winners.';
        if (!unassignedAwardCategories.length) return base;
        const names = unassignedAwardCategories
            .map(c => c.category_title || c.tier_name || 'Untitled prize')
            .slice(0, 4);
        const extra =
            unassignedAwardCategories.length > names.length
                ? ` and ${unassignedAwardCategories.length - names.length} more`
                : '';
        return `${base}\n\nWarning: ${unassignedAwardCategories.length} prize${
            unassignedAwardCategories.length === 1 ? '' : 's'
        } still unassigned (${names.join(', ')}${extra}). Those winners will not be included until you assign them.`;
    }, [unassignedAwardCategories]);

    /** Handle the pagination here */
    // Pagination for submissions data
    const {
        pagination: submissionPagination,
        setPagination: setSubmissionPagination,
        handleRowsChange: handleSubmissionRowsChange,
        handlePageChange: handleSubmissionPageChange,
    } = usePagination(1, 20);

    const handleGetSubmissionData = useCallback(async () => {
        if (!contestId || useParentManageData) return;

        try {
            const response: any = await dispatchPromise(
                getContestManage({
                    contestId: contestId,
                    category: 'submissions',
                    searchTerm: debouncedSearchValue,
                    sortOrder,
                    sortBy,
                    submissionPage: submissionPagination.page,
                    submissionLimit: submissionPagination.rows,
                    ...(selectedCategory !== 'all' && { submissionCategory: selectedCategory }),
                }),
            );

            if (response?.data?.submissions?.pagination) {
                const { currentPage, pageSize, totalCount } = response.data.submissions.pagination;
                setSubmissionPagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount,
                });
            }
        } catch {
            /* saga + globalErrorHandler surface failures */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sortOrder,
        sortBy,
        submissionPagination.page,
        submissionPagination.rows,
        debouncedSearchValue,
        contestId,
        selectedCategory,
        useParentManageData,
        dispatchPromise,
    ]);

    useEffect(() => {
        handleGetSubmissionData();
    }, [handleGetSubmissionData]);

    const parentSearchSyncedRef = useRef(false);
    useEffect(() => {
        if (!useParentManageData) return;
        // Skip mount sync — empty search would refetch manage data and flash the whole page.
        if (!parentSearchSyncedRef.current) {
            parentSearchSyncedRef.current = true;
            return;
        }
        onSearchChange?.(debouncedSearchValue);
    }, [debouncedSearchValue, useParentManageData, onSearchChange]);

    // Create debounced search handler
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            setDebouncedSearchValue(searchValue);
        }, 500),
    ).current;

    // Debounce and update debouncedSearchValue
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchValue]);

    // Clean up debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Handle search input change
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('');
        setDebouncedSearchValue('');
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        // Extract sort field and order from the value
        const [field, order] = value.split('_');

        setSortOrder(order as TCampSortOrder);
        setSortBy(field as TSortBy);
        if (useParentManageData) {
            onSortOrderChange?.(order as 'asc' | 'desc');
            onSortByChange?.(field as TSortBy);
        }
    };

    // Handle category change
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        setSelectedCategory(event.target.value);
    };

    // Handle prize award
    const handlePrizeAward = (awards: any[]) => {
        /** Fail Safe. Better to handle this in the BE — only check prizes in this assignment. */
        const totalFunded = Number(contest?.amount_available) || 0;
        const assigningTotal = (awards || []).reduce((total, item) => {
            const categoryId = getIdForIbjects(item?.award_category) || item?.award_category;
            const category = awardCategories?.categories?.find(c => String(c._id) === String(categoryId));
            return total + (Number(category?.prize) || 0);
        }, 0);

        if (assigningTotal > 0 && assigningTotal > totalFunded) {
            showToastMessage('Total award amount exceeds your current fund, please fix', 'error');
            return;
        }
        dispatch(awardPrize(awards));
    };

    // Handle send winners email
    const handleSendWinnersEmail = () => {
        setWinnersEmailDialogOpen(true);
    };

    // Handle confirmed send winners email
    const handleConfirmedSendWinnersEmail = (isTest: boolean) => {
        if (contestId) {
            dispatch(
                sendWinnersEmail({
                    contestId: contestId,
                    is_test: isTest,
                }),
            );
        }
        setWinnersEmailDialogOpen(false);
    };

    // Handle cancel winners email
    const handleCancelWinnersEmail = () => {
        setWinnersEmailDialogOpen(false);
    };

    const handleViewSubmission = (submission: IManageContestSubmissions) => {
        const project = submission.project;
        if (!project) return;

        dispatch(
            setmedia({
                media: project,
                mediaType: 'act',
            }),
        );

        const videoId = project.video_id ?? (project as { _id?: string })._id;
        if (videoId) {
            dispatch(
                getProjectDetails({
                    id: String(videoId),
                    noPlaylistRefetch: true,
                    justFetchDetails: true,
                    projectToSet: project,
                    skipMediaSet: false,
                }),
            );
            dispatch(setMute(false));
            dispatch(setAutoPlay(true));
        }
    };

    const handleVisitUser = (submission: IManageContestSubmissions) => {
        const route = replaceRouteParam(ROUTES.ESMY_PROFILE, 'profileId?', submission.created_by.userName);
        dispatch(navigate({ routes: route }));
    };

    const handleDownloadTestimonial = async (submission: IManageContestSubmissions) => {
        if (submission.testimonial_video?.project) {
            try {
                const response: any = await dispatchPromise(downloadProject(submission.testimonial_video.project));
                const blob = await response.blob();
                const anchor = document.createElement('a');
                anchor.href = window.URL.createObjectURL(blob);

                // Extract filename from URL or use project title
                let fileName = submission.testimonial_video.project.split('/').pop() || 'testimonial';
                fileName = fileName.split('?')[0];
                try {
                    fileName = decodeURIComponent(fileName);
                } catch {
                    // If decoding fails, use original
                }

                // Ensure .mp4 extension for video
                if (!fileName.endsWith('.mp4')) {
                    fileName = `${fileName}.mp4`;
                }

                anchor.download = fileName;
                anchor.click();
                window.URL.revokeObjectURL(anchor.href);
            } catch (error) {
                console.error('Error downloading testimonial video:', error);
                showToastMessage('Failed to download testimonial video', 'error');
            }
        }
    };

    const handleDownloadAwardPhoto = async (submission: IManageContestSubmissions) => {
        if (submission.submission_picture) {
            try {
                const response: any = await dispatchPromise(downloadProject(submission.submission_picture));
                const blob = await response.blob();
                const anchor = document.createElement('a');
                anchor.href = window.URL.createObjectURL(blob);

                // Extract filename from URL
                let fileName = submission.submission_picture.split('/').pop() || 'award-photo';
                fileName = fileName.split('?')[0];
                try {
                    fileName = decodeURIComponent(fileName);
                } catch {
                    // If decoding fails, use original
                }

                // Determine file extension from blob type or URL
                if (!fileName.includes('.')) {
                    if (blob.type) {
                        const mimeToExt: Record<string, string> = {
                            'image/jpeg': 'jpg',
                            'image/png': 'png',
                            'image/gif': 'gif',
                            'image/webp': 'webp',
                        };
                        const ext = mimeToExt[blob.type];
                        if (ext) {
                            fileName = `${fileName}.${ext}`;
                        }
                    }
                    // If still no extension, default to jpg
                    if (!fileName.includes('.')) {
                        fileName = `${fileName}.jpg`;
                    }
                }

                anchor.download = fileName;
                anchor.click();
                window.URL.revokeObjectURL(anchor.href);
            } catch (error) {
                console.error('Error downloading award photo:', error);
                showToastMessage('Failed to download award photo', 'error');
            }
        }
    };

    const cellTextSx = isBrand
        ? brandContestTableCellTextSx
        : { fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: theme[themeStyle].tag_white };
    const linkCellSx = isBrand
        ? {
              ...brandContestTableCellTextSx,
              cursor: 'pointer',
              '&:hover': { color: BRAND_LUXE_PRIMARY },
          }
        : { ...cellTextSx, cursor: 'pointer', '&:hover': { color: theme[themeStyle].highlight } };

    const columnsData: ColumnsData[] = [
        {
            field: 'project.feature_image',
            label: 'Project',
            type: 'image' as const,
            width: '15%',
            flex: 1,
            sortable: false,
            onClick: (params: IManageContestSubmissions) => {
                dispatch(
                    setmedia({
                        media: params.project,
                        mediaType: 'act',
                    }),
                );
            },
        },
        {
            field: 'project.title',
            label: 'Title',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '25%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography sx={linkCellSx}>{params.project?.title}</Typography>
            ),
        },
        {
            field: 'created_by.userName',
            label: 'Submitted By',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography sx={linkCellSx} onClick={() => handleVisitUser(params)}>
                    @{params.created_by?.userName || 'Unknown User'}
                </Typography>
            ),
            onClick: params => {
                handleVisitUser(params);
            },
        },
        {
            field: 'createdAt',
            label: 'Date Submitted',
            type: 'text' as const,
            textType: 'date' as const,
            width: '20%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography sx={cellTextSx}>{formatDate(params.createdAt, 'MMM D, YY [at] h:mm A')}</Typography>
            ),
        },
        {
            field: 'judging_status',
            label: 'Judging Status',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: false,
        },
        {
            field: 'totalVotes',
            label: 'Votes',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
        },
        {
            field: 'averageJuryScore',
            label: 'Jury Score',
            type: 'text' as const,
            textType: 'regular' as const,
            width: '20%',
            flex: 1,
            sortable: true,
            customCell: (params: IManageContestSubmissions) => (
                <Typography sx={cellTextSx}>{formatJuryScore(params.averageJuryScore)}</Typography>
            ),
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'action' as const,
            width: '5%',
            flex: 1,
            sortable: false,
        },
    ];

    const getMoreMenuItems = (submission: IManageContestSubmissions) => {
        const menuItems = [
            {
                title: 'View Submission',
                id: 'view-submission',
            },
            {
                title: 'Visit User',
                id: 'visit-user',
            },
        ];

        if (submission.payment_due) {
            menuItems.push({
                title: 'Remind to Pay',
                id: 'remind-to-pay',
            });
        }

        // Add download testimonial option if testimonial_video exists
        if (submission.testimonial_video?.project) {
            menuItems.push({
                title: 'Download Testimonial',
                id: 'download-testimonial',
            });
        }

        // Add download award photo option if submission_picture exists
        if (submission.submission_picture) {
            menuItems.push({
                title: 'Download Award Photo',
                id: 'download-award-photo',
            });
        }

        return menuItems;
    };

    const handleRemindToPay = (submission: IManageContestSubmissions) => {
        const id = contestId || contest?._id;
        if (!id || !submission._id) {
            showToastMessage('Unable to send payment reminder for this entry.', 'error');
            return;
        }
        dispatch(
            requestSubmissionPayment({
                contestId: id,
                submissionIds: [submission._id],
            }),
        );
    };

    const handleOnMenuClicks = (type: string, submission: IManageContestSubmissions) => {
        switch (type) {
            case 'view-submission':
                handleViewSubmission(submission);
                break;
            case 'visit-user':
                handleVisitUser(submission);
                break;
            case 'remind-to-pay':
                handleRemindToPay(submission);
                break;
            case 'download-testimonial':
                handleDownloadTestimonial(submission);
                break;
            case 'download-award-photo':
                handleDownloadAwardPhoto(submission);
                break;
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: isBrand ? 'flex-end' : 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}>
                    {!isBrand && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.2rem',
                                color: theme[themeStyle].primary_white,
                            }}>
                            Submissions
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        {preselectionEnabled && (
                            <Tooltip title="Official Selection — review and select entries before jury assignment">
                                <IconButton
                                    aria-label="Official Selection"
                                    onClick={() => setOfficialSelectionOpen(true)}
                                    size="small"
                                    sx={
                                        isBrand
                                            ? {
                                                  bgcolor: BRAND_LUXE_PRIMARY,
                                                  color: '#392700',
                                                  '&:hover': {
                                                      bgcolor: BRAND_LUXE_PRIMARY,
                                                      filter: 'brightness(1.05)',
                                                  },
                                              }
                                            : {
                                                  backgroundColor: theme[themeStyle].highlight,
                                                  color: theme[themeStyle].black,
                                                  '&:hover': {
                                                      backgroundColor: theme[themeStyle].highlight,
                                                      opacity: 0.9,
                                                  },
                                              }
                                    }>
                                    <SelectionIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Award Prizes">
                            <IconButton
                                aria-label="Award Prizes"
                                onClick={() => setPrizeAwardModalOpen(true)}
                                size="small"
                                sx={
                                    isBrand
                                        ? {
                                              bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              color: BRAND_LUXE_PRIMARY,
                                              border: '1px solid rgba(255,201,101,0.25)',
                                              '&:hover': { bgcolor: 'rgba(255,201,101,0.12)' },
                                          }
                                        : {
                                              backgroundColor: theme[themeStyle].primary_orange,
                                              color: theme[themeStyle].black,
                                              '&:hover': {
                                                  backgroundColor: theme[themeStyle].primary_orange,
                                                  opacity: 0.9,
                                              },
                                          }
                                }>
                                <AwardIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title={
                                canSendWinnerEmail
                                    ? 'Send Winners Email'
                                    : 'Assign at least one award to enable this feature'
                            }>
                            <span>
                                <IconButton
                                    aria-label="Send Winners Email"
                                    onClick={handleSendWinnersEmail}
                                    disabled={!canSendWinnerEmail}
                                    size="small"
                                    sx={
                                        isBrand
                                            ? {
                                                  bgcolor: canSendWinnerEmail
                                                      ? BRAND_SURFACE_CONTAINER_HIGHEST
                                                      : 'rgba(255,255,255,0.06)',
                                                  color: canSendWinnerEmail
                                                      ? BRAND_LUXE_PRIMARY
                                                      : 'rgba(255,255,255,0.35)',
                                                  border: canSendWinnerEmail
                                                      ? '1px solid rgba(255,201,101,0.25)'
                                                      : '1px solid rgba(255,255,255,0.08)',
                                                  '&:hover': canSendWinnerEmail
                                                      ? { bgcolor: 'rgba(255,201,101,0.12)' }
                                                      : {},
                                                  '&.Mui-disabled': {
                                                      bgcolor: 'rgba(255,255,255,0.06)',
                                                      color: 'rgba(255,255,255,0.35)',
                                                  },
                                              }
                                            : {
                                                  backgroundColor: canSendWinnerEmail
                                                      ? theme[themeStyle].primary_orange
                                                      : theme[themeStyle].placeholderGray50,
                                                  color: canSendWinnerEmail
                                                      ? theme[themeStyle].black
                                                      : theme[themeStyle].tag_white,
                                                  '&:hover': canSendWinnerEmail
                                                      ? {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            opacity: 0.9,
                                                        }
                                                      : {},
                                                  '&.Mui-disabled': {
                                                      backgroundColor: theme[themeStyle].placeholderGray50,
                                                      color: theme[themeStyle].tag_white,
                                                  },
                                              }
                                    }>
                                    <EmailIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Export CSV">
                            <IconButton
                                aria-label="Export CSV"
                                onClick={() => onExportCSV(submissions)}
                                size="small"
                                sx={
                                    isBrand
                                        ? {
                                              bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                              color: BRAND_LUXE_PRIMARY,
                                              border: '1px solid rgba(255,201,101,0.25)',
                                              '&:hover': { bgcolor: 'rgba(255,201,101,0.12)' },
                                          }
                                        : {
                                              backgroundColor: theme[themeStyle].primary_orange,
                                              color: theme[themeStyle].black,
                                              '&:hover': {
                                                  backgroundColor: theme[themeStyle].primary_orange,
                                                  opacity: 0.9,
                                              },
                                          }
                                }>
                                <ExportIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Search and Filter Controls */}
                {isBrand ? (
                    <Box
                        sx={{
                            mb: 3,
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 1.5,
                            alignItems: { lg: 'center' },
                        }}>
                        <TextField
                            placeholder="Search by title or username"
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            fullWidth
                            sx={{ flex: 1, minWidth: 0, ...brandContestFilterSearchSx }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 22 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchValue ? (
                                    <InputAdornment position="end">
                                        <CloseIcon
                                            sx={{
                                                color: midnight.onSurfaceVariant,
                                                cursor: 'pointer',
                                                fontSize: 20,
                                            }}
                                            onClick={handleClearSearch}
                                        />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Box sx={brandContestFilterFieldsRowSx}>
                            {contest?.categories_enabled && contest?.categories && contest.categories.length > 0 && (
                                <FormControl sx={brandContestFilterControlSx}>
                                    <Select
                                        displayEmpty
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                        inputProps={{ 'aria-label': 'Filter by category' }}
                                        MenuProps={editorialSelectMenuProps}
                                        renderValue={selected => {
                                            const label = selected === 'all' ? 'All Categories' : String(selected);
                                            return `Category: ${label}`;
                                        }}
                                        sx={brandContestFilterSelectSx}>
                                        <MenuItem value="all">All Categories</MenuItem>
                                        {contest.categories.map(category => (
                                            <MenuItem
                                                key={category._id || category.category_name}
                                                value={category.category_name}>
                                                {category.category_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <FormControl sx={brandContestFilterControlSx}>
                                <Select
                                    displayEmpty
                                    value={sortOption}
                                    onChange={handleSortChange}
                                    inputProps={{ 'aria-label': 'Sort submissions' }}
                                    MenuProps={editorialSelectMenuProps}
                                    renderValue={selected => {
                                        const label = SORT_OPTIONS.find(o => o.value === selected)?.label ?? '';
                                        return `Sort: ${label}`;
                                    }}
                                    sx={brandContestFilterSelectSx}>
                                    {SORT_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    placeholder="Search by title or username"
                                    value={searchValue}
                                    onChange={handleSearchInputChange}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchValue ? (
                                            <InputAdornment position="end">
                                                <CloseIcon
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={handleClearSearch}
                                                />
                                            </InputAdornment>
                                        ) : null,
                                        sx: {
                                            backgroundColor: theme[themeStyle].secondaryBg,
                                            borderRadius: 2,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {contest?.categories_enabled &&
                                    contest?.categories &&
                                    contest.categories.length > 0 && (
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    borderRadius: 2,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}>
                                            <InputLabel
                                                id="category-select-label"
                                                sx={{ color: theme[themeStyle].highlight }}>
                                                Category
                                            </InputLabel>
                                            <Select
                                                labelId="category-select-label"
                                                value={selectedCategory}
                                                onChange={handleCategoryChange}
                                                label="Category"
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].primary_white,
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        theme[themeStyle].background_gradient.end,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                                    color: theme[themeStyle].black,
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}>
                                                <MenuItem value="all">All Categories</MenuItem>
                                                {contest.categories.map(category => (
                                                    <MenuItem
                                                        key={category._id || category.category_name}
                                                        value={category.category_name}>
                                                        {category.category_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <FormControl
                                        sx={{
                                            minWidth: 200,
                                            '& .MuiInputBase-root': {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                borderRadius: 2,
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}>
                                        <InputLabel id="sort-select-label" sx={{ color: theme[themeStyle].highlight }}>
                                            Sort By
                                        </InputLabel>
                                        <Select
                                            labelId="sort-select-label"
                                            value={sortOption}
                                            onChange={handleSortChange}
                                            label="Sort By"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].primary_white,
                                                        '& .MuiMenuItem-root': {
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    theme[themeStyle].background_gradient.end,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                                color: theme[themeStyle].black,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                                </InputAdornment>
                                            }>
                                            {SORT_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <Box
                    sx={
                        isBrand
                            ? brandContestTableShellSx
                            : {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                                  borderRadius: 2,
                                  padding: 3,
                                  '& .MuiDataGrid-root': {
                                      backgroundColor: theme[themeStyle].secondaryBg,
                                  },
                                  '& .MuiDataGrid-filler': {
                                      backgroundColor: theme[themeStyle].secondaryBg,
                                  },
                                  '& .MuiDataGrid-virtualScroller': {
                                      backgroundColor: theme[themeStyle].secondaryBg,
                                  },
                              }
                    }>
                    {isBrand ? (
                        <BrandContestSubmissionsList
                            submissions={submissions}
                            formatDateLabel={iso => formatDate(iso, 'MMM D, YY [at] h:mm A')}
                            getMenuItems={getMoreMenuItems}
                            onView={handleViewSubmission}
                            onMenuClick={handleOnMenuClicks}
                            onVisitUser={handleVisitUser}
                            emptyMessage={submissionsEmptyMessage}
                        />
                    ) : (
                        <Table
                            columns={columnsData}
                            staticData={submissions}
                            rowsPerPageOptions={[5, 10, 20, 30]}
                            tableType="submissions"
                            rowHeight={90}
                            headerHeight={65}
                            textSx={{ fontSize: 14 }}
                            sx={{
                                minHeight: 450,
                                backgroundColor: theme[themeStyle].secondaryBg,
                                '& .MuiDataGrid-root': {
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                },
                                '& .MuiDataGrid-filler': {
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                },
                            }}
                            fullscreenMoreIcon
                            fullscreenMoreMenu={getMoreMenuItems}
                            imageType="submissions"
                            onFullscreenMenuClick={handleOnMenuClicks}
                        />
                    )}
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
                        <CustomPagination
                            variant={isBrand ? 'brand' : 'default'}
                            pagination={pagination}
                            onPageChange={useParentManageData ? onPageChange : handleSubmissionPageChange}
                            onRowsChange={useParentManageData ? onRowsChange : handleSubmissionRowsChange}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Prize Award Modal */}
            {contest && awardCategories && (
                <PrizeAwardModal
                    open={prizeAwardModalOpen}
                    onClose={() => setPrizeAwardModalOpen(false)}
                    awardCategories={awardCategories.categories || []}
                    submissions={submissions}
                    contest={contest}
                    existingAwards={managedContest?.awards}
                    onAward={handlePrizeAward}
                />
            )}

            {/* Official Selection Menu — only when pre-selection is enabled on the campaign */}
            {contestId && preselectionEnabled && (
                <OfficialSelectionMenu
                    contestId={contestId}
                    open={officialSelectionOpen}
                    onClose={() => setOfficialSelectionOpen(false)}
                />
            )}

            {/* Send Winners Email Confirmation Dialog */}
            <DialogueSheet
                open={winnersEmailDialogOpen}
                onClose={handleCancelWinnersEmail}
                onAgreeClicked={() => handleConfirmedSendWinnersEmail(true)}
                onDisagreeClick={() => handleConfirmedSendWinnersEmail(false)}
                positiveText="Yes, Send As Test"
                negativeText="No, Send To Real Users"
                header={
                    unassignedAwardCategories.length ? 'Send winners email with pending prizes?' : 'Send Winners Email?'
                }
                body={winnersEmailDialogBody}
            />
        </Box>
    );
};
